import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

// Variants for the NueDesign logo fade/scale
const logoVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.9, ease: 'easeOut' },
  },
};

const AnimatedText = ({ 
  text, 
  duration = 1, 
  delay = 0.5, 
  endColor = "#0a192f", 
  particleColor1 = "#0a192f", 
  particleColor2 = "#FDDD9D", 
  onComplete, 
  style = {},
  ...props 
}) => {
  const controls = useAnimationControls();
  const particleControls = useAnimationControls();
  const textRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  const [svgWidth, setSvgWidth] = useState(300); // math only, not visual min-width
  const [particles, setParticles] = useState([]);
  const [showLogo, setShowLogo] = useState(false);
  const isMounted = useRef(true);

  // track mounted state
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // measure text and generate particles
  useEffect(() => {
    if (textRef.current && text) {
      const bbox = textRef.current.getBBox();
      const w = bbox.width || 300;

      setTextWidth(w + 20);
      setSvgWidth(Math.max(w * 1.2, 300)); // used for particle spread & viewBox only

      const newParticles = [];
      const textCenter = {
        x: bbox.x + bbox.width / 2,
        y: bbox.y + bbox.height / 2,
      };

      for (let i = 0; i < text.length; i++) {
        const letter = textRef.current.getExtentOfChar(i);
        const particleCount = 100;

        for (let j = 0; j < particleCount; j++) {
          newParticles.push({
            x:
              letter.x +
              letter.width / 4 -
              textCenter.x +
              (Math.random() - 0.5) * letter.width * 4,
            y:
              letter.y +
              letter.height / 2 -
              textCenter.y +
              (Math.random() - 0.5) * letter.height * 2,
            size: Math.random() * 3 + 1.5,
            letterIndex: i,
            color: Math.random() < 0.1 ? particleColor1 : particleColor2,
          });
        }
      }

      setParticles(newParticles);
    }
  }, [text, particleColor1, particleColor2]);

  // main animation sequence
  useEffect(() => {
    if (textWidth > 0) {
      const animateText = async () => {
        if (!isMounted.current) return;

        // 1. Wipe in text
        await controls.start({
          clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
          opacity: 1,
          transition: { duration, ease: 'linear', delay },
        });

        if (!isMounted.current) return;

        // 2. Hold text for 1 second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // 3. Wipe out text
        const letterDuration = 0.1;
        const totalDuration = text.length * letterDuration;

        controls.start({
          clipPath: 'inset(0 0 0 100%)',
          transition: { duration: totalDuration, ease: 'linear' },
        });

        // 4. Animate particles flying out, letter by letter
        for (let i = 0; i < text.length; i++) {
          if (!isMounted.current) return;

          particleControls.start((j) => {
            if (j.letterIndex !== i) return {};
            return {
              y: [0, -200 - Math.random() * 100],
              x: [0, 200 + Math.random() * 100],
              opacity: [0, 1, 0],
              transition: {
                duration: 0.6,
                ease: 'easeOut',
                delay: i * letterDuration,
              },
            };
          });

          await new Promise((resolve) => setTimeout(resolve, 10));
        }

        // 5. Wait for COMPLETE particle animation based on text length
        const particleDuration = 0.6;
        const totalParticleTime =
          particleDuration + (text.length - 1) * letterDuration;
        const bufferMs = 500; // small extra delay to guarantee it's after everything

        await new Promise((resolve) =>
          setTimeout(resolve, totalParticleTime * 1000 + bufferMs)
        );

        // 6. Now show logo (actual fade handled by variants)
        setShowLogo(true);
      };

      animateText();
    }
  }, [textWidth, duration, delay, controls, particleControls, text, onComplete]);

  return (
    <div
  style={{
    display: 'inline-block',
    height: 'clamp(110px, 16vw, 170px)',  // more height to avoid clipping
    overflow: 'visible',
  }}
>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} 280`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Main animated text */}
        <motion.text
          ref={textRef}
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily="Raleway-Bold"
          fill={endColor}
          initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)' }}
          animate={controls}
          // responsive font sizing + any additional style from parent
          style={{
          fontSize: 'clamp(2.6rem, 8vw, 5.2rem)',   // was 1.8 / 6 / 3.5
          lineHeight: 1.1,
           ...style,
}}
        >
          {text}
        </motion.text>

        {/* Particles */}
        <motion.g transform={`translate(${svgWidth / 2}, 150)`}>
          {particles.map((particle, index) => (
            <motion.circle
              key={index}
              cx={particle.x}
              cy={particle.y}
              r={particle.size}
              fill={particle.color}
              initial={{ opacity: 0 }}
              animate={particleControls}
              custom={particle}
            />
          ))}
        </motion.g>

        {/* NueDesign Logo - appears only after all effects, with fade + scale */}
        {showLogo && (
          <motion.text
            x="50%"
            y="60%" // slightly lower than main text
            dominantBaseline="central"
            textAnchor="middle"
            fontFamily="'Righteous-Regular', 'Righteous', cursive, sans-serif"
            fill={endColor}
            variants={logoVariants}
            initial="hidden"
            animate="visible"
            style={{
      fontSize: 'clamp(3.8rem, 10vw, 6.2rem)',  // larger min/max + vw
      filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.4))',
      cursor: 'pointer',
    }}
  >
    NueDesign
  </motion.text>
)}
      </svg>
    </div>
  );
};

export default AnimatedText;
